// src/features/equipment/pages/EquipmentDetailsPage/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';

// Hooks
import { useEquipment } from '../../hooks/useEquipment';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../../auth/hooks/usePermissions';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

// Components
import EquipmentHeader from './components/EquipmentHeader';
import EquipmentTabs from './tabs';
import PageHeader from '../../../../components/common/PageHeader';
import MaintenanceCaseDrawer from '../../components/maintenance/MaintenanceCaseDrawer';
import OpenRepairBanner from '../../components/maintenance/OpenRepairBanner';
import { useMaintenanceActions } from '../../components/maintenance/useMaintenanceActions';
import { useEquipmentMaintenance } from '../../hooks/useEquipmentMaintenance';

const OPEN_CASE_STATUSES = ['reported', 'in_progress'];

export default function EquipmentDetailsPage() {
  const { t } = useTranslation(['equipment', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { equipmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useSnackbar();
  const permissions = usePermissions();
  const { navigateBack } = useNavigationStack();

  // State
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchEquipmentById,
    error: equipmentError,
    clearError: clearEquipmentError,
  } = useEquipment();

  const loadEquipmentData = useCallback(async () => {
    if (!equipmentId) return;
    setLoading(true);
    try {
      const equipmentResult = await fetchEquipmentById(equipmentId);
      if (equipmentResult.success) {
        setEquipment(equipmentResult.data);
      } else {
        showError(t('equipment:errors.load_failed'));
      }
    } catch (error) {
      showError(t('equipment:errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [fetchEquipmentById, equipmentId, showError, t]);

  useEffect(() => {
    loadEquipmentData();
  }, [loadEquipmentData]);

  // Maintenance: bumping this reloads the maintenance tab and the open case drawer
  const [maintenanceRefreshKey, setMaintenanceRefreshKey] = useState(0);
  const openCaseId = searchParams.get('case');
  const currentTab = searchParams.get('tab');

  // Open faults and repairs, for the status chip, the banner and the report dialog
  const { listCases } = useEquipmentMaintenance();
  const [openCases, setOpenCases] = useState([]);
  useEffect(() => {
    if (!equipmentId) return undefined;
    let cancelled = false;
    listCases(equipmentId, { status: OPEN_CASE_STATUSES, limit: 200 }).then((result) => {
      if (!cancelled && result.success) setOpenCases(result.data.items || []);
    });
    return () => { cancelled = true; };
  }, [equipmentId, listCases, maintenanceRefreshKey]);

  const openCase = useCallback((maintenanceId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'maintenance');
      next.set('case', maintenanceId);
      return next;
    });
  }, [setSearchParams]);

  const closeCase = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('case');
      return next;
    });
  }, [setSearchParams]);

  const handleMaintenanceChanged = useCallback(async (maintenanceCase) => {
    // Refresh the header status without the full-page spinner
    const result = await fetchEquipmentById(equipmentId);
    if (result.success) setEquipment(result.data);
    setMaintenanceRefreshKey((k) => k + 1);
    if (maintenanceCase?.maintenance_id) openCase(maintenanceCase.maintenance_id);
  }, [fetchEquipmentById, equipmentId, openCase]);

  const maintenanceActions = useMaintenanceActions({
    equipment, openCases, onChanged: handleMaintenanceChanged, onOpenCase: openCase,
  });

  const handleBack = useCallback(() => {
    const fallbackPath = equipment?.room_id
      ? `/rooms/${equipment.room_id}`
      : '/equipment';
    navigateBack(fallbackPath);
  }, [navigateBack, equipment]);

  const handleEdit = useCallback(() => {
    navigate(`/equipment/${equipmentId}/edit`);
  }, [navigate, equipmentId]);

  const canUpdate = permissions.hasScope('equipment:update');

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!equipment) {
    return (
      <Box>
        <PageHeader
          onBack={handleBack}
          title={t('equipment:messages.equipment_not_found')}
        />
        <Alert severity="error">
          {t('equipment:messages.equipment_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <EquipmentHeader
        equipment={equipment}
        canUpdate={canUpdate}
        onBack={handleBack}
        onEdit={handleEdit}
        maintenanceActions={maintenanceActions}
        openCases={openCases}
      />

      {/* The repairs tab lists the open cases itself */}
      {currentTab !== 'maintenance' && (
        <OpenRepairBanner
          equipment={equipment}
          openCases={openCases}
          onOpenCase={openCase}
          onShowAll={() => setSearchParams({ tab: 'maintenance' })}
        />
      )}

      {equipmentError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearEquipmentError}>
          {equipmentError}
        </Alert>
      )}

      <EquipmentTabs
        equipmentId={equipmentId}
        equipment={equipment}
        maintenanceActions={maintenanceActions}
        maintenanceRefreshKey={maintenanceRefreshKey}
        onOpenMaintenanceCase={openCase}
      />

      <MaintenanceCaseDrawer
        maintenanceId={openCaseId}
        refreshKey={maintenanceRefreshKey}
        onClose={closeCase}
        actions={maintenanceActions}
        openCases={openCases}
        onOpenCase={openCase}
      />
      {maintenanceActions.dialogs}
    </Box>
  );
}
